/**
 * Turning a query result's raw columns and rows into table columns.
 *
 * One function, because the same result has to read identically wherever it
 * is shown — in a chat, or pinned to a dashboard. The decisions here are the
 * subtle kind that drift apart if they are copied: whether a column of digits
 * is an amount or a label, whether text that looks numeric should be lined up
 * on the right, whether a percent arrived as 0.42 or as 42. Two copies of
 * that reasoning means the same query slowly starts looking like two
 * different results.
 *
 * This is app-level rather than kit: it knows the shape a warehouse query
 * comes back in. The kit's `ResultTable` takes what this produces.
 */
import type { ResultColumn } from '../../ui-kit';
import { inferColumnMeta } from './fieldTypes';
import type { ColumnMeta } from './types';
import { formatForSort, formatPlainText } from './format';

/** What the backend knows about the columns, when it says anything at all. */
export interface ColumnHints {
  columnTypes?: Record<string, string>;
  columnSemantics?: Record<string, string>;
}

const NUMERIC_TYPES = new Set(['number', 'currency', 'percent']);
/** A plain decimal written as text: "1813", "-0.25", "2.26". Not "00123", not "1e5". */
const NUMBER_TEXT = /^-?(0|[1-9]\d*)(\.\d+)?$/;
/** Names whose digits are labels, not amounts — never given thousands separators. */
const LABEL_NUMBER_NAME = /(^|_)(id|ids|year|yr|zip|zipcode|postal|code|phone|number|no)($|_)/i;

export const toNumber = (v: unknown) => (typeof v === 'string' && NUMBER_TEXT.test(v.trim()) ? Number(v) : v);

/**
 * Warehouses often send numbers as text ("1813"), which reads as a word
 * column: left-aligned, no separators, sorted like words. A column whose every
 * value is a plain number is treated as numbers — unless its name says the
 * digits are a label (an id, a year, a zip code).
 */
function numericIfNumbers(name: string, values: unknown[], meta: ColumnMeta): ColumnMeta {
  // Digits that are a label — an id, a year, a zip code — read as written,
  // whether they arrive as numbers or as text: "196739900994", not "196,739,900,994".
  if (meta.type === 'id' || (LABEL_NUMBER_NAME.test(name) && meta.type !== 'currency' && meta.type !== 'percent')) {
    return { ...meta, type: 'text', align: 'left' };
  }
  if (NUMERIC_TYPES.has(meta.type)) return meta;
  if (['date', 'datetime', 'boolean', 'json'].includes(meta.type)) return meta;
  const present = values.filter((v) => v !== null && v !== undefined && v !== '');
  const allNumbers =
    present.length > 0 &&
    present.every((v) => (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && NUMBER_TEXT.test(v.trim())));
  if (!allNumbers) return meta;
  // A name that says percent keeps that meaning once the values are numbers.
  if (/(pct|percent|percentage)/i.test(name)) {
    const max = Math.max(...present.map((v) => Math.abs(Number(v))));
    return { ...meta, type: 'percent', percentScale: max <= 1 ? 'fraction' : 'whole', align: 'right' };
  }
  return { ...meta, type: 'number', align: 'right' };
}

/** The columns for a result's rows: how each one is named, aligned, read and sorted. */
export function buildResultColumns(
  columns: string[],
  rows: unknown[][],
  hints: ColumnHints = {},
): ResultColumn[] {
  return columns.map((name, i) => {
    const meta = numericIfNumbers(
      name,
      rows.map((r) => r[i]),
      inferColumnMeta(name, i, rows, hints),
    );
    const numeric = NUMERIC_TYPES.has(meta.type);
    const value = (v: unknown) => (numeric ? toNumber(v) : v);
    return {
      key: name,
      label: meta.label,
      numeric,
      format: (v: unknown) => formatPlainText(value(v), meta) || '—',
      sortValue: (v: unknown) => (v === null || v === undefined || v === '' ? null : formatForSort(value(v), meta)),
    };
  });
}
